import * as Sentry from '@sentry/react';
import { formatDateOnlyLocal, parseDateOnlyLocal } from '@erp/shared-utils';
import { SalesOrderSource } from 'src/types/application/SalesOrderDraftDTO';

/**
 * Floor height (px) for the line-items table on the **enhanced** draft view,
 * whose table keeps its own scroll area (sticky header + expand-to-fullscreen
 * are built around it).
 *
 * Each product occupies two rows — the line row plus the "Selected Product"
 * match row — so five products need roughly 330px of body on top of the
 * table header, the section title and the box padding. Below this the table
 * showed two or three products and read as broken.
 *
 * The v2 `SalesOrderDraftModal` deliberately does NOT use this: there the
 * table is content-sized and the left column is the single scroll container,
 * because a capped table inside a scrolling column produced nested
 * scrollbars.
 */
export const LINE_ITEMS_TABLE_MIN_HEIGHT_PX = 480;

/**
 * True when a draft line resolves to a product.
 *
 * Three identifiers count, and any one of them is enough:
 * `selectedTranslationListItemId` (EDI — the Famous translation-list entry, and
 * the whole product reference for an EDI line), `selectedCustomerProductId` and
 * `selectedProductVariantId` (email / file-upload / manual).
 *
 * The EDI case is the one that gets dropped: the Famous product catalog syncs
 * on its own schedule, so a correctly mapped EDI line routinely has no variant
 * and no customer product. Checking only those two calls a perfectly orderable
 * line unmatched — which, depending on the caller, blocks approval outright or
 * silently drops the line from the order.
 *
 * Every caller that asks "is this line matched?" must use this, so the answer
 * cannot differ between the banner, the status icon and the approval gates.
 */
export function isDraftLineMatched(item: {
  selectedTranslationListItemId?: string | null;
  selectedProductVariantId?: string | null;
  selectedCustomerProductId?: string | null;
}): boolean {
  return Boolean(
    item.selectedTranslationListItemId ||
    item.selectedProductVariantId ||
    item.selectedCustomerProductId,
  );
}

/**
 * Source flags for determining the type of sales order source
 */
export interface SourceFlags {
  isEdiSource: boolean;
  isEmailSource: boolean;
  isDirectEntry: boolean;
  isFileUpload: boolean;
  isErpSync: boolean;
}

/**
 * Exhaustively determine source type flags - will cause compilation error if new source added
 */
export function getSourceFlags(
  source: SalesOrderSource | undefined,
): SourceFlags {
  if (!source) {
    return {
      isEdiSource: false,
      isEmailSource: false,
      isDirectEntry: false,
      isFileUpload: false,
      isErpSync: false,
    };
  }

  switch (source) {
    case SalesOrderSource.Edi:
      return {
        isEdiSource: true,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: false,
        isErpSync: false,
      };
    case SalesOrderSource.Email:
      return {
        isEdiSource: false,
        isEmailSource: true,
        isDirectEntry: false,
        isFileUpload: false,
        isErpSync: false,
      };
    case SalesOrderSource.DirectEntry:
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: true,
        isFileUpload: false,
        isErpSync: false,
      };
    case SalesOrderSource.FileUpload:
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: true,
        isErpSync: false,
      };
    case SalesOrderSource.ErpSync:
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: false,
        isErpSync: true,
      };
    default: {
      // Exhaustiveness check - will cause TS error if a new source is added
      const exhaustiveCheck: never = source;
      const error = new Error(`Unhandled SalesOrderSource: ${exhaustiveCheck}`);
      console.error(error);
      Sentry.captureException(error);
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: false,
        isErpSync: false,
      };
    }
  }
}

/**
 * Extract email domain from an email address string
 */
export function extractEmailDomain(input?: string): string | undefined {
  if (!input) return undefined;
  const match = input.toLowerCase().match(/@([^>\s]+)/);
  return match?.[1];
}

/**
 * Format a Date to YYYY-MM-DD string
 */
export function formatDateOnly(d: Date): string {
  return formatDateOnlyLocal(d);
}

/**
 * Parse a YYYY-MM-DD string to Date (at midnight local time)
 */
export function parseDateOnlyString(s: string): Date {
  return parseDateOnlyLocal(s) ?? new Date(Number.NaN);
}

/**
 * Parse a Date, string, or null/undefined to Date or undefined
 */
export function parseDateOnly(value?: Date | string | null): Date | undefined {
  if (!value) return undefined;
  const dateString = typeof value === 'string' ? value : formatDateOnly(value);
  return parseDateOnlyString(dateString);
}

/**
 * Check if a date is in the past (only relevant when editable)
 */
export function isDatePast(date: Date | string, isEditable: boolean): boolean {
  const dateOnly = typeof date === 'string' ? date : formatDateOnly(date);
  const compare = parseDateOnlyString(dateOnly);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isEditable ? compare < today : false;
}

/**
 * Whether an admin may delete an already-processed draft.
 *
 * Approved and rejected drafts normally have no Delete action at all. EDI is
 * the exception: those drafts are created by the trading partner and can
 * auto-approve before anyone sees them, so a duplicate or malformed 850 would
 * otherwise have no way out. Manually keyed and email-ingested orders keep the
 * normal reject/cancel flow.
 *
 * UX only — `evaluateDraftDeletion` on the backend is what enforces this.
 */
export function canDeleteProcessedDraft({
  hasLoadedDraft,
  isProcessed,
  isEdiSource,
  isAdminView,
}: {
  hasLoadedDraft: boolean;
  isProcessed: boolean;
  isEdiSource: boolean;
  isAdminView: boolean;
}): boolean {
  return hasLoadedDraft && isProcessed && isEdiSource && isAdminView;
}
